import { useState } from "react";

import {
  isSuspended,
  labeebStandingPath,
  type LabeebStanding,
} from "../../api/labeeb";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../states";
import { Badge } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { WriteResult } from "../write/EffectTiming";

/**
 * Suspend a business in Labeeb, or lift its suspension.
 *
 * LABEEB'S FIRST REAL ENFORCEMENT LEVER. Until its admin API was replaced the
 * console could only narrow a merchant's capability; `docs/products/labeeb.md`
 * carried a section insisting Labeeb had no blocking mechanism and that the
 * console must not imply one — true then, and false now. A suspended business
 * has its NEXT request refused.
 *
 * That section's other warning still holds: the sibling `LabeebUserMgmt`
 * stack's `PUT /admin/users/{userId}` accepts `status: BLOCKED`, writes a
 * label to a table the agent path never reads, and returns 200. That is the
 * fake. This is not it.
 *
 * THE VERSION IS READ HERE, not passed in. `PATCH /standing` requires the
 * `expected_version` the operator actually saw, so the control that shows the
 * status is the one that must send it — handing a version down through props
 * from a different read is how a stale number reaches a write.
 *
 * Rendered in two places (the merchant page and /policy/access) on purpose:
 * one write, two doors, matching how MCP blocking already works here.
 */
export function LabeebStandingControl({
  config,
  tenantId,
  compact = false,
}: {
  config: AppConfig;
  tenantId: string;
  /** Drops the explanatory copy, for a table cell. */
  compact?: boolean;
}) {
  const standing = useResource<LabeebStanding>(config, labeebStandingPath(tenantId), [tenantId]);
  const write = useWrite<LabeebStanding>(config);
  const [confirming, setConfirming] = useState(false);
  const s = standing.state;
  const busy = write.state.kind === "submitting";

  const current = s.kind === "ready" ? s.data : null;
  const suspended = isSuspended(current);
  const version = current?.version;

  function submit(reason: string) {
    // `expected_version` is REQUIRED and must be the version just read. When
    // the document has moved on, the API answers 409 version_conflict with
    // the version it holds now — surfaced below as a re-read prompt rather
    // than a dead end, because the operator's intent is still valid.
    if (version === undefined) return;
    void write
      .submit(labeebStandingPath(tenantId), {
        method: "PATCH",
        body: JSON.stringify({
          expected_version: version,
          reason,
          status: suspended ? "active" : "suspended",
        }),
      })
      .then((r) => {
        if (!r) return;
        standing.reload();
      });
    setConfirming(false);
  }

  return (
    <>
      {s.kind === "loading" && <Loading what="standing" />}
      {s.kind === "signedout" && <SignedOut />}
      {s.kind === "denied" && <Denied message={s.error.message} />}
      {s.kind === "unavailable" && (
        <CouldNotLook what="standing" message={s.error.message} onRetry={standing.reload} />
      )}
      {s.kind === "notfound" && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Labeeb has no record of this merchant, so there is nothing to suspend.
        </p>
      )}
      {(s.kind === "invalid" || s.kind === "error") && (
        <LoadFailed what="standing" message={s.error.message} />
      )}

      {s.kind === "ready" && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            {suspended ? <Badge tone="red">suspended</Badge> : <Badge>active</Badge>}
            {!compact && (
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {suspended
                  ? "Labeeb refuses this business's next request."
                  : "Labeeb is serving this business."}
              </span>
            )}
          </div>

          {/* Attribution for the last change. Null until a business has been
              suspended at least once, which is why it is conditional rather
              than rendered as three dashes. */}
          {!compact && (current?.by || current?.at || current?.reason) && (
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              Last changed{current.by ? ` by ${current.by}` : ""}
              {current.at ? ` at ${current.at}` : ""}
              {current.reason ? ` — "${current.reason}"` : ""}
            </p>
          )}

          <Gate need="block:write">
            <div className="mt-3">
              <Button
                color={suspended ? undefined : "red"}
                outline={suspended}
                disabled={busy || version === undefined}
                onClick={() => setConfirming(true)}
              >
                {suspended ? "Lift suspension…" : "Suspend…"}
              </Button>
            </div>
          </Gate>
        </>
      )}

      {write.state.kind === "done" && (
        <div className="mt-3">
          <WriteResult
            title={suspended ? "Suspension lifted" : "Suspended"}
            onDismiss={write.reset}
          >
            <p className="text-sm">
              {suspended
                ? "Labeeb serves this business again from its next request."
                : "Labeeb refuses this business from its next request."}
            </p>
          </WriteResult>
        </div>
      )}

      {/* THE THREE 409s ARE NOT ONE ERROR. Each needs different words, and
          collapsing them into "refused" is what the old single GuardRefused
          state did. */}
      {write.state.kind === "versionConflict" && (
        <div className="mt-3">
          <WriteResult tone="amber" title="Somebody changed this first" onDismiss={write.reset}>
            <p className="text-sm">
              Nothing was written. This merchant&rsquo;s standing has moved on
              {write.state.currentVersion !== undefined
                ? ` to version ${write.state.currentVersion}`
                : ""}
              , so the change was refused rather than applied over someone else&rsquo;s.
            </p>
            <div className="mt-2">
              <Button outline onClick={() => { write.reset(); standing.reload(); }}>
                Re-read and try again
              </Button>
            </div>
          </WriteResult>
        </div>
      )}
      {write.state.kind === "alreadyInState" && (
        <div className="mt-3">
          {/* NOT a failure. Reporting "refused" here sends an operator
              hunting for a problem that does not exist. */}
          <WriteResult title="Already the case" onDismiss={write.reset}>
            <p className="text-sm">
              This merchant is already {suspended ? "active" : "suspended"}. Nothing
              needed changing.
            </p>
          </WriteResult>
        </div>
      )}
      {write.state.kind === "idempotencyConflict" && (
        <div className="mt-3">
          <WriteResult tone="amber" title="Not written" onDismiss={write.reset}>
            <p className="text-sm">
              A request key was reused for a different change, so nothing was written.
              Retry; if this repeats, it is a bug in this console, not in your change.
            </p>
          </WriteResult>
        </div>
      )}
      {write.state.kind === "rateLimited" && (
        <div className="mt-3">
          <WriteResult tone="amber" title="Too many changes just now" onDismiss={write.reset}>
            <p className="text-sm">
              The gateway is throttling requests, not Labeeb. Nothing was written — wait a
              moment and retry.
            </p>
          </WriteResult>
        </div>
      )}
      {write.state.kind === "invalid" && (
        <div className="mt-3">
          <WriteResult tone="amber" title="Refused" onDismiss={write.reset}>
            <p className="text-sm">{write.state.error.message}</p>
          </WriteResult>
        </div>
      )}
      {write.state.kind === "idleReauth" && (
        <div className="mt-3">
          <WriteResult tone="amber" title="Nothing was written" onDismiss={write.reset}>
            <p className="text-sm">
              This session has been idle, so the change was not sent. Confirming your
              identity now — retry once you are back.
            </p>
          </WriteResult>
        </div>
      )}
      {write.state.kind === "signedout" && (
        <div className="mt-3">
          <WriteResult tone="amber" title="Not written" onDismiss={write.reset}>
            <p className="text-sm">Your session ended. Sign in again and retry.</p>
          </WriteResult>
        </div>
      )}
      {(write.state.kind === "guardRefused" ||
        write.state.kind === "denied" ||
        write.state.kind === "notfound" ||
        write.state.kind === "unavailable" ||
        write.state.kind === "error") && (
        <div className="mt-3">
          <WriteResult tone="amber" title="Not written" onDismiss={write.reset}>
            <p className="text-sm">{write.state.error.message}</p>
          </WriteResult>
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        title={suspended ? "Lift this suspension" : "Suspend this merchant in Labeeb"}
        confirmLabel={suspended ? "Lift suspension" : "Suspend"}
        destructive={!suspended}
        requireReason
        reasonHint={
          suspended ? "e.g. DA-1234, merchant resolved the dispute" : "e.g. DA-1234, abuse report"
        }
        busy={busy}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
        effect={
          suspended
            ? "Labeeb serves this business again from its next request."
            : "Labeeb refuses this business from its next request — not at the end of its session."
        }
      >
        {suspended ? (
          <p>
            The assistant starts answering this merchant again. Anyone at the business
            can use it, subject to their own standing.
          </p>
        ) : (
          <>
            <p>
              The assistant stops answering <span className="ref">{tenantId}</span>{" "}
              entirely. Every user at this business is refused, including its owner.
            </p>
            <p>
              This does not touch MCP. A merchant suspended here can still be using
              MCP tools, and the two are blocked separately.
            </p>
          </>
        )}
      </ConfirmDialog>
    </>
  );
}
